import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { AssetService } from "../../assets/asset.service";
import { AssetServiceUI } from "../../assets/asset.service.ui";
import { Asset } from "../../assets/asset.type";
import { TicketService } from "../ticket.service";
import { Ticket } from "../ticket.type";
import { CreateTicketService } from "./create-ticket.service";

@Component({
  selector: "app-create-ticket",
  templateUrl: "./create-ticket.component.html",
  styleUrls: ["./create-ticket.component.css"],
  providers: [CreateTicketService],
})
export class CreateTicketComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  questions: QuestionBase<string | boolean>[];
  assetId: string;
  asset: Asset;

  constructor(
    public location: Location,
    private router: Router,
    private loader: LoadingService,
    private ticketService: TicketService,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private questionsService: CreateTicketService,
    private authenticationService: AuthenticationService,
    private assetService: AssetService,
    private assetServiceUI: AssetServiceUI,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  ngOnInit(): void {
    this.assetId = this.route.snapshot.queryParams["assetId"];

    if (this.assetId) {
      this.getAsset();
    } else {
      setTimeout(() => this.loader.hide());
    }

    this.questionsService
      .generateQuestions({
        ...(this.currentUser.isCompany() && {
          companyValue: this.currentUser.selectedCompanyId || this.currentUser.companyId,
          companySkip: true,
          partnerSkip: true,
        }),
        ...(this.currentUser.isPartner() && {
          partnerValue: this.currentUser.partnerId,
          partnerSkip: true,
        }),
        ...(this.assetId && {
          assetSkip: true,
          companySkip: true,
          partnerSkip: true,
        }),
        onboardingSkip: !this.currentUser.isGlobal(),
      })
      .subscribe((qs) => {
        this.questions = qs;
      });
  }

  goBack(): void {
    this.location.back();
  }

  getAsset(): void {
    this.assetService
      .getAsset(this.assetId)
      .subscribe(
        (asset: Asset) => {
          this.asset = asset;
        },
        (error) => {
          this.notificationService.error(error || "Couldn't find Asset");
          this.router.navigate(["/assets"]);
        },
      )
      .add(() => this.loader.hide());
  }

  onSubmit(values: Ticket): void {
    const ticketData = { assetId: this.assetId, ...values };
    this.loader.show();
    this.ticketService
      .createTicket(ticketData)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (ticket: any) => {
          this.notificationService.success(`Ticket ${ticket.zendeskId} has been created`);
          this.router.navigate([`/tickets/${ticket.id}`]);
        },
        (error) => {
          this.notificationService.error(error);
        },
      )
      .add(() => this.loader.hide());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async onChange(event: AnyObject) {
    const assetId = this.dynamicForm.form.get("assetId")?.value;
    if (event.source?.id === "assetId") {
      this.assetService
        .getAsset(assetId, { relations: { contract: { maintenanceProvider: true } } })
        .subscribe((asset: Asset) => {
          if (!asset.contract?.maintenanceProvider?.isChannelWorks) {
            this.assetServiceUI.showTicketCreationWarningModal(asset);
            this.enableOrDisableTicketFields("disable");
            this.dynamicForm.saveDisabled = true;
          } else {
            this.enableOrDisableTicketFields("enable");
            this.dynamicForm.saveDisabled = false;
          }
          this.dynamicForm.form.get("assetOem").setValue(asset?.type.model.oem.name);
          this.dynamicForm.form.get("assetModel").setValue(asset?.type.model.name);
          this.dynamicForm.form.get("assetType").setValue(asset?.type.name);
          this.dynamicForm.getQuestion("assetOem").hide = false;
          this.dynamicForm.getQuestion("assetModel").hide = false;
          this.dynamicForm.getQuestion("assetType").hide = false;
        });
    } else if (!assetId && this.dynamicForm.getQuestion("assetOem")) {
      this.dynamicForm.getQuestion("assetOem").hide = true;
      this.dynamicForm.getQuestion("assetModel").hide = true;
      this.dynamicForm.getQuestion("assetType").hide = true;
    }
  }

  private enableOrDisableTicketFields(action: "enable" | "disable") {
    const fields = ["subject", "description", "failureType", "priority"];
    if (this.currentUser.isGlobal()) {
      fields.push("onboarding");
    }
    fields.forEach((key: string) => {
      this.dynamicForm.form.get(key)[action]();
    });
  }
}
